'use client';

import { motion, useAnimation } from 'motion/react';
import { useEffect } from 'react';

import { cn } from '@/lib/utils';

type BoxesIconProps = {
  className?: string;
  isAnimating?: boolean;
  size?: number;
};

const BoxesIcon = ({ isAnimating, className, size = 16 }: BoxesIconProps) => {
  const controls = useAnimation();

  useEffect(() => {
    if (isAnimating === undefined) {
      return;
    }
    if (isAnimating) {
      controls.start('animate');
    } else {
      controls.start('normal');
    }
  }, [isAnimating, controls]);

  const pathTransition = {
    duration: 0.3,
    ease: 'easeOut' as const,
  };

  return (
    <motion.svg
      animate={controls}
      aria-label='Boxes Icon'
      className={cn(className)}
      fill='none'
      height={size}
      initial='normal'
      role='img'
      stroke='currentColor'
      strokeLinecap='round'
      strokeLinejoin='round'
      strokeWidth='2'
      style={{ overflow: 'visible' }}
      variants={{
        normal: { scale: 1 },
        animate: {
          scale: 1.05,
          transition: { duration: 0.3, ease: 'easeOut' },
        },
      }}
      viewBox='0 0 24 24'
      width={size}
      xmlns='http://www.w3.org/2000/svg'
    >
      <motion.path
        animate={controls}
        d='M2.97 12.92A2 2 0 0 0 2 14.63v3.24a2 2 0 0 0 .97 1.71l3 1.8a2 2 0 0 0 2.06 0L12 19v-5.5l-5-3-4.03 2.42Z m4.03 3.58 -4.74 -2.85 m4.74 2.85 5-3 m-5 3v5.17'
        variants={{
          normal: { translateX: 0, translateY: 0 },
          animate: {
            translateX: -1.5,
            translateY: 1.5,
            transition: pathTransition,
          },
        }}
      />
      <motion.path
        animate={controls}
        d='M12 13.5V19l3.97 2.38a2 2 0 0 0 2.06 0l3-1.8a2 2 0 0 0 .97-1.71v-3.24a2 2 0 0 0-.97-1.71L17 10.5l-5 3Z m5 3-5-3 m5 3 4.74-2.85 M17 16.5v5.17'
        variants={{
          normal: { translateX: 0, translateY: 0 },
          animate: {
            translateX: 1.5,
            translateY: 1.5,
            transition: pathTransition,
          },
        }}
      />
      <motion.path
        animate={controls}
        d='M7.97 4.42A2 2 0 0 0 7 6.13v4.37l5 3 5-3V6.13a2 2 0 0 0-.97-1.71l-3-1.8a2 2 0 0 0-2.06 0l-3 1.8Z M12 8 7.26 5.15 m4.74 2.85 4.74-2.85 M12 13.5V8'
        variants={{
          normal: { translateX: 0, translateY: 0 },
          animate: {
            translateX: 0,
            translateY: -1.5,
            transition: pathTransition,
          },
        }}
      />
    </motion.svg>
  );
};

BoxesIcon.displayName = 'BoxesIcon';

export { BoxesIcon };
